import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import UndoIcon from "@mui/icons-material/Undo";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";

const Payouts = () => {
  const {
    getBalance,
    getPayouts,
    createPayout,
    getTransactions,
    getTransactionDetails,
    refundTransaction,
    patchUserProduct,
    retryFailedTransaction,
    trackFailedTransaction,
    getPaymentMethods,
  } = useApi();
  const { stripeAccountId, isCoach, stripeIsSetup, paymentsProEnabled } =
    useUserInfo();
  const [tabValue, setTabValue] = useState(0);
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(30, "days"));
  const [endDate, setEndDate] = useState(moment());
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [cancelSubscription, setCancelSubscription] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [refundQuantity, setRefundQuantity] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundDialogLoading, setRefundDialogLoading] = useState(false);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  useEffect(() => {
    if (isCoach) {
      loadBalance();
      loadPayouts();
    }
  }, [isCoach]);

  useEffect(() => {
    if (tabValue === 1) {
      loadTransactions();
    }
  }, [tabValue, startDate, endDate]);

  const loadBalance = async () => {
    try {
      const data = await getBalance(stripeAccountId);
      setBalance(data);
    } catch (err) {
      console.error("Failed to load balance:", err);
      setError("Failed to load balance");
    } finally {
      setLoading(false);
    }
  };

  const loadPayouts = async () => {
    try {
      const data = await getPayouts(stripeAccountId, 50);
      setPayouts(data.data || []);
    } catch (err) {
      console.error("Failed to load payouts:", err);
      setError("Failed to load payouts");
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const data = await getTransactions({
        stripeAccountId,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
      });
      setTransactions(data.data || []);
    } catch (err) {
      console.error("Failed to load transactions:", err);
      setError("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    const amountInCents = Math.round(parseFloat(payoutAmount) * 100);
    const availableAmount =
      balance?.available?.find((b) => b.currency === "usd")?.amount || 0;

    if (amountInCents > availableAmount) {
      setError(
        `Insufficient balance. Available: $${(availableAmount / 100).toFixed(
          2
        )}`
      );
      return;
    }

    try {
      setPayoutLoading(true);
      setError("");
      await createPayout({
        stripeAccountId,
        amount: amountInCents,
        currency: "usd",
      });
      setPayoutDialogOpen(false);
      setPayoutAmount("");
      await loadBalance();
      await loadPayouts();
    } catch (err) {
      console.error("Failed to create payout:", err);
      setError(err.message || "Failed to create payout");
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleRefundClick = async (transaction) => {
    setSelectedTransaction(transaction);
    setRefundDialogOpen(true);
    setRefundDialogLoading(true);
    try {
      // Get transaction details to check for subscription
      const details = await getTransactionDetails(
        transaction.id,
        stripeAccountId,
        "charge"
      );
      // Merge transaction with details - details has { transaction: {...}, subscription: {...} }
      // Ensure subscription is properly set (could be null or an object)
      const mergedTransaction = {
        ...transaction,
        ...(details.transaction || {}),
        subscription: details.subscription || null,
        purchasedProduct: details.purchasedProduct || null,
      };
      setSelectedTransaction(mergedTransaction);
      setRefundAmount((transaction.amount / 100).toFixed(2));
      setRefundQuantity(
        details.purchasedProduct?.quantityLeft?.toString() ?? ""
      );
    } catch (err) {
      console.error("Failed to load transaction details:", err);
      setError("Failed to load transaction details");
      setRefundDialogOpen(false);
      setSelectedTransaction(null);
    } finally {
      setRefundDialogLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!selectedTransaction) return;
    if (selectedTransaction.refunded) return;

    try {
      setRefundLoading(true);
      setError("");
      await refundTransaction({
        chargeId: selectedTransaction.id,
        amount: refundAmount ? parseFloat(refundAmount) : undefined,
        cancelSubscription,
        cancelImmediately,
        stripeAccountId,
      });
      if (
        selectedTransaction.purchasedProduct &&
        refundQuantity !== "" &&
        refundQuantity !== undefined
      ) {
        const qty = parseInt(refundQuantity, 10);
        if (!isNaN(qty) && qty >= 0) {
          await patchUserProduct({
            userId: selectedTransaction.purchasedProduct.userId,
            priceId: selectedTransaction.purchasedProduct.priceId,
            action: "updateQuantity",
            value: qty,
          });
        }
      }
      setRefundDialogOpen(false);
      setSelectedTransaction(null);
      setRefundAmount("");
      setRefundQuantity("");
      setCancelSubscription(false);
      setCancelImmediately(false);
      await loadTransactions();
    } catch (err) {
      console.error("Failed to refund:", err);
      setError(err.message || "Failed to process refund");
    } finally {
      setRefundLoading(false);
    }
  };

  const handleRetryClick = async (transaction) => {
    // Track the failure first
    try {
      await trackFailedTransaction({
        chargeId: transaction.id,
        reason: "Payment failed - manual retry requested",
        stripeAccountId,
      });
    } catch (err) {
      console.error("Failed to track failure:", err);
    }

    setSelectedTransaction(transaction);
    setRetryDialogOpen(true);
    setSelectedPaymentMethodId("");

    // Fetch payment methods for the customer
    if (transaction.customer) {
      try {
        setLoadingPaymentMethods(true);
        const pmResponse = await getPaymentMethods(
          transaction.customer,
          stripeAccountId
        );
        setPaymentMethods(pmResponse?.paymentMethods || []);
        // Set default payment method if available
        if (pmResponse?.defaultPaymentMethodId) {
          setSelectedPaymentMethodId(pmResponse.defaultPaymentMethodId);
        } else if (pmResponse?.paymentMethods?.length > 0) {
          setSelectedPaymentMethodId(pmResponse.paymentMethods[0].id);
        }
      } catch (err) {
        console.error("Failed to load payment methods:", err);
        setError("Failed to load payment methods");
      } finally {
        setLoadingPaymentMethods(false);
      }
    } else {
      setError("No customer associated with this transaction");
    }
  };

  const handleRetry = async () => {
    if (!selectedTransaction || !selectedPaymentMethodId) {
      setError("Please select a payment method");
      return;
    }

    try {
      setRetryLoading(true);
      setError("");
      await retryFailedTransaction({
        chargeId: selectedTransaction.id,
        paymentMethodId: selectedPaymentMethodId,
        stripeAccountId,
      });
      setRetryDialogOpen(false);
      setSelectedTransaction(null);
      setSelectedPaymentMethodId("");
      setPaymentMethods([]);
      await loadTransactions();
    } catch (err) {
      console.error("Failed to retry payment:", err);
      setError(err.message || "Failed to retry payment");
    } finally {
      setRetryLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp) => {
    return moment.unix(timestamp).format("MMM D, YYYY");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
      case "succeeded":
        return "success";
      case "pending":
        return "warning";
      case "failed":
      case "canceled":
        return "error";
      default:
        return "default";
    }
  };

  if (!isCoach) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          This page is for coaches. Please sign in as a coach to view payouts.
        </Alert>
      </Box>
    );
  }

  if (stripeIsSetup && !paymentsProEnabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              component={Link}
              to="/coach-subscription"
            >
              Subscribe
            </Button>
          }
        >
          Payments Pro is required for payouts and transactions. Subscribe to
          Payments Pro to unlock this feature.
        </Alert>
      </Box>
    );
  }

  if (!stripeIsSetup) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="info"
          action={
            paymentsProEnabled && (
              <Button
                color="inherit"
                size="small"
                component={Link}
                to="/coaching/create-account"
              >
                Connect Stripe
              </Button>
            )
          }
        >
          {paymentsProEnabled
            ? "You have access to payments but need to connect your Stripe account to receive payouts and manage transactions."
            : "Set up your Stripe account to receive payouts and manage transactions."}
        </Alert>
      </Box>
    );
  }

  const availableBalance =
    balance?.available?.find((b) => b.currency === "usd")?.amount || 0;
  const pendingBalance =
    balance?.pending?.find((b) => b.currency === "usd")?.amount || 0;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Payouts & Transactions
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
        >
          <Tab label="Payouts" />
          <Tab label="Transactions" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ mt: 3 }}>
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Balance
              </Typography>
              <Box sx={{ display: "flex", gap: 4 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Available
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(availableBalance)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(pendingBalance)}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => setPayoutDialogOpen(true)}
                disabled={availableBalance === 0}
              >
                Request Payout
              </Button>
            </Card>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Method</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : payouts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">
                          No payouts found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    payouts.map((payout) => (
                      <TableRow key={payout.id}>
                        <TableCell>{formatDate(payout.created)}</TableCell>
                        <TableCell>
                          {formatCurrency(payout.amount, payout.currency)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payout.status}
                            color={getStatusColor(payout.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {payout.destination?.last4
                            ? `****${payout.destination.last4}`
                            : payout.method || "N/A"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ mt: 3 }}>
            <Card sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  maxDate={endDate}
                  slotProps={{ textField: { size: "small" } }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  minDate={startDate}
                  maxDate={moment()}
                  slotProps={{ textField: { size: "small" } }}
                />
                <Button
                  variant="outlined"
                  onClick={loadTransactions}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
            </Card>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary">
                          No transactions found for selected date range
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{formatDate(transaction.created)}</TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.type}
                            size="small"
                            color={
                              transaction.type === "charge"
                                ? "primary"
                                : "secondary"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {transaction.customerName || "N/A"}
                        </TableCell>
                        <TableCell>
                          {transaction.paymentMethodDetails ? (
                            transaction.paymentMethodDetails.type === "card" ? (
                              <Typography variant="body2">
                                {transaction.paymentMethodDetails.brand?.toUpperCase() ||
                                  "Card"}{" "}
                                •••• {transaction.paymentMethodDetails.last4}
                              </Typography>
                            ) : (
                              <Typography variant="body2">
                                {transaction.paymentMethodDetails.type}
                              </Typography>
                            )
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell>
                          {transaction.description || "N/A"}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            transaction.amount,
                            transaction.currency
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.status}
                            color={getStatusColor(transaction.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "center",
                            }}
                          >
                            {transaction.type === "charge" && (
                              <>
                                <Tooltip title="Refund">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      handleRefundClick(transaction)
                                    }
                                    disabled={
                                      transaction.status === "failed" ||
                                      transaction.refunded === true
                                    }
                                  >
                                    <UndoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {transaction.status === "failed" && (
                                  <Tooltip title="Retry Payment">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        handleRetryClick(transaction)
                                      }
                                    >
                                      <RefreshIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Request Payout Dialog */}
        <Dialog
          open={payoutDialogOpen}
          onClose={() => {
            setPayoutDialogOpen(false);
            setPayoutAmount("");
            setError("");
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Request Payout</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Available Balance: {formatCurrency(availableBalance)}
            </Typography>
            <TextField
              label="Amount"
              type="number"
              value={payoutAmount}
              onChange={(e) => setPayoutAmount(e.target.value)}
              fullWidth
              required
              inputProps={{
                min: 0.01,
                step: 0.01,
                max: availableBalance / 100,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              helperText={`Maximum: $${(availableBalance / 100).toFixed(2)}`}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPayoutDialogOpen(false);
                setPayoutAmount("");
                setError("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestPayout}
              variant="contained"
              disabled={payoutLoading || !payoutAmount}
            >
              {payoutLoading ? "Processing..." : "Request Payout"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Refund Confirmation Dialog */}
        <Dialog
          open={refundDialogOpen}
          onClose={() => {
            if (!refundLoading && !refundDialogLoading) {
              setRefundDialogOpen(false);
              setSelectedTransaction(null);
              setRefundAmount("");
              setRefundQuantity("");
              setCancelSubscription(false);
              setCancelImmediately(false);
              setRefundDialogLoading(false);
              setError("");
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {refundDialogLoading
              ? "Loading..."
              : selectedTransaction?.refunded
                ? "Already Refunded"
                : selectedTransaction?.subscription
                  ? "Refund & Cancel Subscription?"
                  : "Confirm Refund"}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {refundDialogLoading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center", py: 4 }}
              >
                <CircularProgress />
              </Box>
            ) : selectedTransaction?.refunded ? (
              <Alert severity="info">
                This transaction has already been refunded. No further action is
                needed.
              </Alert>
            ) : (
              selectedTransaction && (
                <>
                  <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Transaction Details
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Amount:</strong>{" "}
                      {formatCurrency(
                        selectedTransaction.amount,
                        selectedTransaction.currency
                      )}
                    </Typography>
                    {selectedTransaction.customerName && (
                      <Typography variant="body2">
                        <strong>Customer:</strong>{" "}
                        {selectedTransaction.customerName}
                      </Typography>
                    )}
                    {selectedTransaction.paymentMethodDetails && (
                      <Typography variant="body2">
                        <strong>Payment Method:</strong>{" "}
                        {selectedTransaction.paymentMethodDetails.type ===
                        "card"
                          ? `${
                              selectedTransaction.paymentMethodDetails.brand?.toUpperCase() ||
                              "Card"
                            } •••• ${
                              selectedTransaction.paymentMethodDetails.last4
                            }`
                          : selectedTransaction.paymentMethodDetails.type}
                      </Typography>
                    )}
                    {selectedTransaction.description && (
                      <Typography variant="body2">
                        <strong>Description:</strong>{" "}
                        {selectedTransaction.description}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <TextField
                  label="Refund Amount"
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />

                {selectedTransaction.purchasedProduct && (
                  <TextField
                    label="Remaining quantity (purchased product)"
                    type="number"
                    value={refundQuantity}
                    onChange={(e) => setRefundQuantity(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    inputProps={{ min: 0, step: 1 }}
                    helperText="Update the quantity remaining for this one-time purchase after the refund"
                  />
                )}

                {selectedTransaction.subscription &&
                selectedTransaction.subscription !== null &&
                typeof selectedTransaction.subscription === "object" ? (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      This transaction is associated with a recurring
                      subscription.
                    </Alert>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cancelSubscription}
                          onChange={(e) =>
                            setCancelSubscription(e.target.checked)
                          }
                        />
                      }
                      label="Also cancel the subscription"
                    />
                    {cancelSubscription && (
                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Cancellation Timing</InputLabel>
                        <Select
                          value={cancelImmediately ? "immediate" : "period_end"}
                          onChange={(e) =>
                            setCancelImmediately(e.target.value === "immediate")
                          }
                          label="Cancellation Timing"
                        >
                          <MenuItem value="period_end">
                            Cancel at end of billing period (customer keeps
                            access until period ends)
                          </MenuItem>
                          <MenuItem value="immediate">
                            Cancel immediately (customer loses access right
                            away)
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    This is a one-time payment. The refund will be processed
                    immediately.
                  </Typography>
                )}
                </>
              )
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRefundDialogOpen(false);
                setSelectedTransaction(null);
                setRefundAmount("");
                setRefundQuantity("");
                setCancelSubscription(false);
                setCancelImmediately(false);
                setRefundDialogLoading(false);
                setError("");
              }}
              disabled={refundLoading || refundDialogLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRefund}
              variant="contained"
              color="error"
              disabled={
                refundLoading ||
                refundDialogLoading ||
                selectedTransaction?.refunded
              }
            >
              {refundLoading ? "Processing..." : "Confirm Refund"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Retry Dialog */}
        <Dialog
          open={retryDialogOpen}
          onClose={() => {
            setRetryDialogOpen(false);
            setSelectedTransaction(null);
            setSelectedPaymentMethodId("");
            setPaymentMethods([]);
            setError("");
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Retry Failed Payment</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selectedTransaction && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Transaction:{" "}
                  {formatCurrency(
                    selectedTransaction.amount,
                    selectedTransaction.currency
                  )}
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This will cancel any automatic retry attempts by Stripe and
                  attempt to charge the customer with the selected payment
                  method.
                </Alert>
                {loadingPaymentMethods ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : paymentMethods.length === 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No payment methods found for this customer. Please ask the
                    customer to add a payment method.
                  </Alert>
                ) : (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Payment Method</InputLabel>
                    <Select
                      value={selectedPaymentMethodId}
                      onChange={(e) =>
                        setSelectedPaymentMethodId(e.target.value)
                      }
                      label="Select Payment Method"
                    >
                      {paymentMethods.map((pm) => (
                        <MenuItem key={pm.id} value={pm.id}>
                          {pm.card
                            ? `${pm.card.brand.toUpperCase()} •••• ${
                                pm.card.last4
                              } Exp: ${pm.card.exp_month}/${pm.card.exp_year}`
                            : `${pm.type} •••• ${pm.card?.last4 || "N/A"}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRetryDialogOpen(false);
                setSelectedTransaction(null);
                setSelectedPaymentMethodId("");
                setPaymentMethods([]);
                setError("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRetry}
              variant="contained"
              disabled={
                retryLoading ||
                !selectedPaymentMethodId ||
                paymentMethods.length === 0
              }
            >
              {retryLoading ? "Processing..." : "Retry Payment"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Payouts;
